// Admin view — manage profiles.role for the team.
//
// Editor-only. Reads `profiles` joined to `auth.users.email` via a public
// view we expose specifically for this admin path. Renders a table with
// per-row role dropdowns; writes go through the admin-set-role Edge
// Function so the authz check stays server-side.

import { sb } from "../lib/db.ts";
import type { Role } from "../lib/auth.ts";

const escHtml = (s: string | null | undefined) =>
  String(s ?? "").replace(/[&<>"']/g, (c) =>
    ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]!),
  );

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string;

interface ProfileRow {
  id: string;
  email: string | null;
  display_name: string | null;
  initials: string | null;
  role: Role;
  created_at: string;
}

const ROLES: Role[] = ["viewer", "contributor", "approver", "editor"];

async function setRole(user_id: string, role: Role): Promise<void> {
  const { data: { session } } = await sb.auth.getSession();
  if (!session) throw new Error("not signed in");
  const res = await fetch(`${SUPABASE_URL}/functions/v1/admin-set-role`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${session.access_token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ user_id, role }),
  });
  const text = await res.text();
  if (!res.ok) {
    let msg = text;
    try { msg = JSON.parse(text).error ?? text; } catch { /* */ }
    throw new Error(`admin-set-role: ${res.status} — ${msg}`);
  }
}

export async function renderAdmin(host: HTMLElement, callerRole: Role): Promise<() => void> {
  host.innerHTML = "";
  if (callerRole !== "editor") {
    host.innerHTML = `<div class="viz-empty">Editor role required to manage team. Your role: ${callerRole}.</div>`;
    return () => { host.innerHTML = ""; };
  }

  // The `profiles_with_email` view (added in 0003_admin.sql) exposes
  // auth.users.email next to the profile so editors can find users by name
  // instead of UUID.
  const { data, error } = await sb
    .from("profiles_with_email")
    .select("*")
    .order("role")
    .order("display_name");
  if (error) {
    host.innerHTML = `<div class="viz-empty">Profiles load failed: ${escHtml(error.message)}</div>`;
    return () => { host.innerHTML = ""; };
  }
  const rows = (data as ProfileRow[]) ?? [];

  host.innerHTML = `
    <div class="admin-wrap">
      <p class="muted">Editor-only. Promote/demote teammates here. Self-edit is forbidden — ask another editor if you need to step down.</p>
      <table class="ops-table">
        <thead><tr>
          <th>User</th><th>Email</th><th>Current</th><th>Set role</th><th></th>
        </tr></thead>
        <tbody>${rows.map((r) => `
          <tr data-id="${escHtml(r.id)}">
            <td>${escHtml(r.display_name ?? "(no display name)")}${r.initials ? ` <span class="muted">(${escHtml(r.initials)})</span>` : ""}</td>
            <td>${escHtml(r.email ?? "—")}</td>
            <td><span class="kind-chip role-${escHtml(r.role)}">${escHtml(r.role)}</span></td>
            <td>
              <select class="role-select" data-user="${escHtml(r.id)}">
                ${ROLES.map((role) =>
                  `<option value="${role}"${role === r.role ? " selected" : ""}>${role}</option>`,
                ).join("")}
              </select>
            </td>
            <td><button class="btn-set-role" data-user="${escHtml(r.id)}">Apply</button></td>
          </tr>`).join("")}
        </tbody>
      </table>
    </div>
  `;

  host.querySelectorAll<HTMLButtonElement>(".btn-set-role").forEach((btn) => {
    btn.addEventListener("click", async () => {
      const userId = btn.dataset.user!;
      const select = host.querySelector<HTMLSelectElement>(`.role-select[data-user="${userId}"]`);
      if (!select) return;
      const role = select.value as Role;
      btn.disabled = true; btn.textContent = "…";
      try {
        await setRole(userId, role);
        // Update the chip in place.
        const row = host.querySelector<HTMLTableRowElement>(`tr[data-id="${userId}"]`);
        const chip = row?.querySelector<HTMLElement>(".kind-chip");
        if (chip) {
          chip.className = `kind-chip role-${role}`;
          chip.textContent = role;
        }
        btn.textContent = "Applied ✓";
        setTimeout(() => { btn.textContent = "Apply"; btn.disabled = false; }, 1500);
      } catch (e) {
        alert(`Role change failed: ${String(e)}`);
        btn.textContent = "Apply"; btn.disabled = false;
      }
    });
  });

  return () => { host.innerHTML = ""; };
}
